import React, { useRef, useState, Dispatch, SetStateAction } from 'react'
import {
  BodyText,
  HeadingText,
  Tooltip,
  PaypalAppIcon,
  InvestIcon,
  ShippingIcon,
  UndoIcon
} from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import { get } from 'lodash'
import Logo from '../Logo/Logo'
import { StoreDetail, Action } from '../../../types/ShoppingPageType'
import ActionButton from '../ActionButton/ActionButton'
import ActionLink from '../ActionLink/ActionLink'
import ActionModal from '../ActionModal/ActionModal'
import { sendFptiClick } from '../../../../../lib/fpti-pa'
import { SHOPPING_STORE_SHOP_PRESSED } from '../../../../../lib/client-constants'
import useWorldReady, { FormatMessage } from 'hooks/useWorldReady'
type ItemType =
  | 'averageSavings30d'
  | 'freeShipping'
  | 'freeReturn'
  | 'payInFourPayments'

interface Props {
  // TODO: https://engineering.paypalcorp.com/jira/browse/DTSHOPATMS-2243
  // eslint-disable-next-line react/no-unused-prop-types
  isVisible?: boolean
  storeDetails?: StoreDetail
}

interface HeaderItemProps {
  cashback: string | null
  title?: string
  itemType: ItemType
  isVisible?: boolean
  formatMessage: FormatMessage
}

interface ItemIconProps {
  itemType: ItemType
}

interface TermsAndExclusionsTextProps {
  isRewardsStore: boolean
  storeActions: Action[] | []
  setExclusionsModal: Dispatch<SetStateAction<boolean>>
}

interface RewardsPointsInfoProps {
  isRewardsStore: boolean
  rewardsPointsLabel: string | null
  rewardsPointsSubtitle: string | null
}

interface DoubleGoldLabelProps {
  isVisible: boolean
  content: string | null
}

const ItemIcon = ({ itemType }: ItemIconProps) => {
  switch (itemType) {
    case 'averageSavings30d':
      return (
        <InvestIcon size="sm" className="store-details-header__item-icon" />
      )
    case 'freeShipping':
      return (
        <ShippingIcon size="sm" className="store-details-header__item-icon" />
      )
    case 'freeReturn':
      return <UndoIcon size="sm" className="store-details-header__item-icon" />
    case 'payInFourPayments':
      return (
        <PaypalAppIcon size="sm" className="store-details-header__item-icon" />
      )

    default:
      return (
        <PaypalAppIcon size="sm" className="store-details-header__item-icon" />
      )
  }
}

const HeaderItem = ({
  isVisible = true,
  cashback,
  title,
  itemType,
  formatMessage
}: HeaderItemProps) => {
  const tooltipContainerRef = useRef(null)

  if (!itemType || !isVisible) {
    return null
  }

  const description = formatMessage(
    'main.storeDetails.header.averageSavingsTooltip',
    { cashback }
  )
  if (itemType === 'averageSavings30d' && cashback !== '') {
    title = `${title}: ${cashback}`
  }

  return (
    <div
      className="store-details-header__item"
      data-cy={`store-details-header__item-${itemType}`}
      ref={tooltipContainerRef}
    >
      <ItemIcon itemType={itemType} />
      {title}
      {itemType === 'averageSavings30d' ? (
        <Tooltip
          theme="v2"
          tooltipClassName="store-details-header__tooltipContent"
          triggerClassName="store-details-header__tooltip"
          name="averageSavingsTooltip"
          containerRef={tooltipContainerRef}
          description={description}
        />
      ) : null}
    </div>
  )
}

const TermsAndExclusionsText = ({
  isRewardsStore,
  storeActions,
  setExclusionsModal
}: TermsAndExclusionsTextProps) => {
  if (!isRewardsStore || !storeActions || !storeActions[1]) {
    return null
  }
  const exclusionsLinkAction = get(storeActions, '[1]') as Action | undefined
  const termsLinkAction = get(storeActions, '[2]') as Action | undefined

  const exclusionsLinkText = (
    <ActionLink
      className="store-details-header__terms-and-exclusion-link"
      actions={exclusionsLinkAction ? [exclusionsLinkAction] : null}
      onClick={() => {
        setExclusionsModal(true)
      }}
    />
  )
  const termsLinkText = (
    <ActionLink
      className="store-details-header__terms-and-exclusion-link"
      actions={termsLinkAction ? [termsLinkAction] : null}
    />
  )

  return (
    <Message
      id="main.storeDetails.header.termsAndExclusions"
      termsLinkText={termsLinkText}
      exclusionsLinkText={exclusionsLinkText}
    />
  )
}

const DoubleGoldLabel = ({
  isVisible = false,
  content
}: DoubleGoldLabelProps) => {
  if (!isVisible) {
    return null
  }
  return (
    <span className="store-details-header__rewards-subtitle">{content}</span>
  )
}

const RewardsPointsInfo = ({
  isRewardsStore,
  rewardsPointsLabel,
  rewardsPointsSubtitle
}: RewardsPointsInfoProps) => {
  if (!rewardsPointsLabel || !isRewardsStore) {
    return null
  }

  return (
    <div className="store-details-header__rewards-container">
      <BodyText type="strong" className="store-details-header__rewards-label">
        {rewardsPointsLabel}
      </BodyText>
      <DoubleGoldLabel
        isVisible={!!rewardsPointsSubtitle}
        content={rewardsPointsSubtitle}
      />
    </div>
  )
}

const HeaderBanner = ({ storeDetails }: Props) => {
  const secondaryImageUrl = get(storeDetails, 'secondaryImage.url')
  if (!secondaryImageUrl) {
    return null
  }

  return (
    <img
      className="store-details-header__cover"
      src={secondaryImageUrl}
      alt={get(storeDetails, 'secondaryImage.accessibilityLabel')}
    />
  )
}

export const StoreDetailsHeader = ({
  isVisible = true,
  storeDetails
}: Props) => {
  const { formatMessage } = useWorldReady()

  const [isExclusionsOpen, setIsExclusionsOpen] = useState<boolean>(false)

  if (!storeDetails || !isVisible) {
    return null
  }

  const isRewardsStore = get(storeDetails, 'itemType') === 'REWARDS_STORE'
  const rewardsPointsLabel = get(storeDetails, 'rewardsPointsLabel', null)
  const rewardsPointsSubtitle = get(storeDetails, 'rewardsPointsSubtitle', null)
  const hasNoBanner =
    !storeDetails.secondaryImage || !storeDetails.secondaryImage.url
  const containerClassName = hasNoBanner
    ? 'store-details-header__container--no-banner'
    : 'store-details-header__container'
  const logoClassName = hasNoBanner
    ? 'store-details-header__logo--no-banner'
    : 'store-details-header__logo'
  const contentClassName = hasNoBanner
    ? 'store-details-header__content--no-banner'
    : 'store-details-header__content'
  const headerItemsTypes: ItemType[] = [
    'averageSavings30d',
    'freeShipping',
    'freeReturn',
    'payInFourPayments'
  ]

  const handleShopNowButtonClick = () => {
    sendFptiClick({
      event_name: SHOPPING_STORE_SHOP_PRESSED,
      store_id: get(storeDetails, 'storeIds[1]'), // Honey store id
      cashback_enabled: !!storeDetails.rewardsPointsLabel, // Cashback enabled indicatorof the stores
      cashback_amt: storeDetails.rewardsPointsLabel || '',
      merchant_id: get(storeDetails, 'storeIds[0]'),
      merchant_name: storeDetails.title,
      link_name: 'shop'
    })
  }

  return (
    <div className="store-details-header" data-cy="store-profile-page-header">
      <ActionModal
        isOpen={isExclusionsOpen}
        setIsOpen={setIsExclusionsOpen}
        modalContent={get(storeDetails, 'actions[1].modalContent')}
      />
      <HeaderBanner storeDetails={storeDetails} />
      <div className={containerClassName} data-cy="store-details-header__logo">
        <Logo
          className={logoClassName}
          src={get(storeDetails, 'primaryImage.url')}
          alt={get(storeDetails, 'primaryImage.accessibilityLabel')}
          size="lg"
        />
        <div
          className={contentClassName}
          data-cy="store-details-header__content"
        >
          <HeadingText className="store-details-header__title" size="sm">
            {storeDetails.title}
          </HeadingText>
          <RewardsPointsInfo
            isRewardsStore={isRewardsStore}
            rewardsPointsLabel={rewardsPointsLabel}
            rewardsPointsSubtitle={rewardsPointsSubtitle}
          />
          <BodyText className="store-details-header__description">
            <TermsAndExclusionsText
              storeActions={get(storeDetails, 'actions') || []}
              isRewardsStore={isRewardsStore}
              setExclusionsModal={setIsExclusionsOpen}
            />
          </BodyText>
          <BodyText className="store-details-header__items">
            {headerItemsTypes.map(itemType => {
              const item = get(storeDetails, itemType, null)
              if (!get(item, 'title')) {
                return null
              }

              return (
                <HeaderItem
                  key={itemType}
                  itemType={itemType}
                  cashback={get(item, 'content') || ''}
                  title={get(item, 'title')}
                  formatMessage={formatMessage}
                />
              )
            })}
          </BodyText>
          <div
            className="store-details-header__action"
            data-cy="store-details-header__action"
          >
            <ActionButton
              actions={get(storeDetails, 'actions')}
              onClick={handleShopNowButtonClick}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
